/**
 * CierreMensualPage — reporte contable dual FSA / UNE para asientos mensuales.
 *
 * Audience-aware (mismo route, contenido distinto):
 *   - BACKOFFICE (Fiado SA) → Libro FSA: cobrado, transferido a UNE, retencion FSA
 *   - PARTNER (UNE) → Libro UNE: fondeado (salidas), cobros recibidos (entradas)
 *
 * Master-detail: panel izquierdo mensual, panel derecho drill-down por mes.
 * Boton Exportar CSV con el mes seleccionado.
 *
 * Base flujo: todo por sent_at del SPEI (Mexico SPEI = instantaneo).
 */

import { useState, useMemo } from 'react';
import { RefreshCw, Download } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { formatCurrency } from '@/utils/formatters';
import { ROUTES, PARTNER_ID } from '@/constants';
import { useAudience } from '@/hooks/useAudience';
import { getAccountingMonthly, getAccountingMonthDetail } from '@/services/credit-worker.service';
import type {
  AccountingMonthlyFsaResponse,
  AccountingMonthlyUneResponse,
  AccountingMonthDetailFsaResponse,
  AccountingMonthDetailUneResponse,
  FsaMonthlySummary,
  UneMonthlySummary,
  FsaTransferDetail,
  FsaFundingInDetail,
  UneFundingOutDetail,
  UneCobroInDetail,
} from '@/types/accounting.types';

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Fondeo', path: ROUTES.FONDEO },
  { label: 'Cierre Mensual', path: ROUTES.CIERRE_MENSUAL },
];

const money = (n: number) => formatCurrency(n, 'MXN', 'es-MX');

const MONTH_LABELS_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
function formatYyyyMm(yyyyMm: string): string {
  const [y, m] = yyyyMm.split('-');
  const idx = parseInt(m!, 10) - 1;
  return `${MONTH_LABELS_ES[idx] ?? m} ${y}`;
}

function formatDateTimeShort(iso: string): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }).format(d);
  } catch {
    return iso.substring(0, 16);
  }
}

// ─── CSV helpers ─────────────────────────────────────────────────────────

function downloadCsv(filename: string, rows: (string | number)[][]) {
  const csv = rows.map(r =>
    r.map(cell => {
      const s = String(cell ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')
  ).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

// ─── FSA Views ───────────────────────────────────────────────────────────

function FsaMonthlyTable({ months, selectedYm, onSelect }: {
  months: FsaMonthlySummary[]; selectedYm: string | null; onSelect: (ym: string) => void;
}) {
  if (months.length === 0) return <p className="py-8 text-center text-sm text-muted-foreground">Sin datos para el año seleccionado.</p>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-muted-foreground">
          <th className="px-2 py-1.5 font-medium">Mes</th>
          <th className="px-1.5 py-1.5 text-right font-medium text-emerald-700 dark:text-emerald-400">Recibido<br/>UNE</th>
          <th className="px-1.5 py-1.5 text-right font-medium text-blue-700 dark:text-blue-400">Transfer<br/>UNE</th>
          <th className="px-1.5 py-1.5 text-right font-medium">Neto<br/>SPEI</th>
          <th className="px-1.5 py-1.5 text-right font-medium">Cobrado<br/>cliente</th>
          <th className="px-1.5 py-1.5 text-right font-medium text-emerald-700 dark:text-emerald-400">Int<br/>FSA</th>
          <th className="px-1.5 py-1.5 text-right font-medium text-amber-700 dark:text-amber-400">IVA</th>
          <th className="px-2 py-1.5 text-right font-medium text-emerald-700 dark:text-emerald-400">Retención<br/>FSA</th>
        </tr>
      </thead>
      <tbody>
        {months.map(m => (
          <tr key={m.yyyy_mm} onClick={() => onSelect(m.yyyy_mm)}
            className={`cursor-pointer border-b border-border/20 transition-colors ${m.yyyy_mm === selectedYm ? 'bg-primary/5' : 'hover:bg-muted/40'}`}
            style={{ height: 34 }}>
            <td className="whitespace-nowrap px-2 py-0 text-xs font-medium text-foreground">{formatYyyyMm(m.yyyy_mm)}</td>
            <td className="px-1.5 py-0 text-right tabular-nums text-xs text-emerald-700 dark:text-emerald-400">{money(m.funding_in_mxn)}</td>
            <td className="px-1.5 py-0 text-right tabular-nums text-xs font-semibold text-blue-700 dark:text-blue-400">{money(m.total_transferred_to_une_mxn)}</td>
            <td className={`px-1.5 py-0 text-right tabular-nums text-xs font-semibold ${m.net_spei_flow_mxn < 0 ? 'text-rose-700 dark:text-rose-400' : 'text-emerald-700 dark:text-emerald-400'}`}>{money(m.net_spei_flow_mxn)}</td>
            <td className="px-1.5 py-0 text-right tabular-nums text-xs">{money(m.total_collected_from_client_mxn)}</td>
            <td className="px-1.5 py-0 text-right tabular-nums text-xs text-emerald-700 dark:text-emerald-400">{money(m.total_fsa_interest_mxn)}</td>
            <td className="px-1.5 py-0 text-right tabular-nums text-xs text-amber-700 dark:text-amber-400">{money(m.total_iva_mxn)}</td>
            <td className="px-2 py-0 text-right tabular-nums text-xs font-semibold text-emerald-700 dark:text-emerald-400">{money(m.total_retained_by_fsa_mxn)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function FsaMonthDetailPanel({ yyyyMm, data }: { yyyyMm: string; data: AccountingMonthDetailFsaResponse | undefined }) {
  const transfers = data?.transfers ?? [];
  const fundings = data?.fundings_in ?? [];
  const totals = useMemo(() => transfers.reduce((acc, t) => ({
    collected: acc.collected + t.total_collected_from_client_mxn,
    transferred: acc.transferred + t.transferred_to_une_mxn,
    principal: acc.principal + t.principal_mxn,
    fsa: acc.fsa + t.fsa_interest_mxn,
    une: acc.une + t.une_interest_mxn,
    iva: acc.iva + t.iva_mxn,
    retained: acc.retained + t.retained_by_fsa_mxn,
  }), { collected: 0, transferred: 0, principal: 0, fsa: 0, une: 0, iva: 0, retained: 0 }), [transfers]);
  const totalFundingIn = fundings.reduce((s, f) => s + f.total_amount_mxn, 0);

  const exportCsv = () => {
    const rows: (string | number)[][] = [
      ['Tipo', 'Fecha SPEI', 'ID', 'Referencia', 'Ventana', 'Créditos', 'Records', 'Total cobrado', 'Principal', 'Int FSA', 'Int UNE', 'IVA', 'Transfer UNE', 'Retención FSA', 'Status'],
      ...fundings.map(f => ['FONDEO_ENTRADA', f.sent_at, f.funding_id, f.bank_reference ?? '', f.window ?? '', f.credits_count, '', f.total_amount_mxn, 0, 0, 0, 0, 0, 0, f.status]),
      ...transfers.map(t => ['TRANSFER_SALIDA', t.sent_at, t.transfer_id, t.bank_reference ?? '', '', t.credits_count, t.records_count,
        t.total_collected_from_client_mxn, t.principal_mxn, t.fsa_interest_mxn,
        t.une_interest_mxn, t.iva_mxn, t.transferred_to_une_mxn, t.retained_by_fsa_mxn, t.status,
      ]),
    ];
    downloadCsv(`libro-fsa-${yyyyMm}.csv`, rows);
  };

  const hasData = transfers.length > 0 || fundings.length > 0;
  if (!hasData) return <p className="py-12 text-center text-sm text-muted-foreground">Sin movimientos en {formatYyyyMm(yyyyMm)}.</p>;

  return (
    <div>
      <div className="flex items-center justify-between border-b border-border bg-muted/30 px-3 py-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Movimientos FSA de {formatYyyyMm(yyyyMm)}</h3>
        <Button variant="outline" onClick={exportCsv}><Download size={14} /> CSV</Button>
      </div>

      {/* Fondeos UNE→FSA (entradas) */}
      {fundings.length > 0 && (
        <div>
          <div className="border-b border-border/60 bg-emerald-50/40 dark:bg-emerald-950/20 px-3 py-1.5">
            <span className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400 font-medium">Fondeos SPEI UNE→FSA (entradas)</span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-1.5 font-medium">Fecha SPEI</th>
                <th className="px-2 py-1.5 font-medium">Funding ID</th>
                <th className="px-2 py-1.5 font-medium">Ventana</th>
                <th className="px-2 py-1.5 text-center font-medium">Créd</th>
                <th className="px-2 py-1.5 font-medium">Ref bancaria</th>
                <th className="px-2 py-1.5 text-right font-medium text-emerald-700 dark:text-emerald-400">Monto</th>
              </tr>
            </thead>
            <tbody>
              {fundings.map((f: FsaFundingInDetail) => (
                <tr key={f.funding_id} className="border-b border-border/20" style={{ height: 32 }}>
                  <td className="px-3 py-0 text-xs">{formatDateTimeShort(f.sent_at)}</td>
                  <td className="px-2 py-0 text-xs font-mono text-muted-foreground">{f.funding_id.slice(0, 8)}...</td>
                  <td className="px-2 py-0 text-xs">{f.window ?? '—'}</td>
                  <td className="px-2 py-0 text-center text-xs tabular-nums">{f.credits_count}</td>
                  <td className="px-2 py-0 text-xs font-mono text-muted-foreground">{(f.bank_reference ?? '').slice(0, 14) || '—'}</td>
                  <td className="px-2 py-0 text-right tabular-nums text-xs font-semibold text-emerald-700 dark:text-emerald-400">{money(f.total_amount_mxn)}</td>
                </tr>
              ))}
              <tr className="border-t border-border/40 bg-emerald-50/20 dark:bg-emerald-950/10">
                <td className="px-3 py-1.5 text-xs font-semibold" colSpan={5}>Total fondeos recibidos ({fundings.length})</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-xs font-bold text-emerald-700 dark:text-emerald-400">{money(totalFundingIn)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Transferencias FSA→UNE (salidas) */}
      {transfers.length > 0 && (
        <div>
          <div className="border-t border-b border-border/60 bg-blue-50/40 dark:bg-blue-950/20 px-3 py-1.5">
            <span className="text-[10px] uppercase tracking-wider text-blue-700 dark:text-blue-400 font-medium">Transferencias SPEI FSA→UNE (salidas)</span>
          </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-1.5 font-medium">Fecha SPEI</th>
            <th className="px-2 py-1.5 font-medium">Ref</th>
            <th className="px-2 py-1.5 text-center font-medium">Cred</th>
            <th className="px-2 py-1.5 text-right font-medium">Total cobrado</th>
            <th className="px-2 py-1.5 text-right font-medium">Principal</th>
            <th className="px-2 py-1.5 text-right font-medium text-emerald-700 dark:text-emerald-400">Int FSA</th>
            <th className="px-2 py-1.5 text-right font-medium text-blue-700 dark:text-blue-400">Int UNE</th>
            <th className="px-2 py-1.5 text-right font-medium text-amber-700 dark:text-amber-400">IVA</th>
            <th className="px-2 py-1.5 text-right font-medium text-blue-700 dark:text-blue-400">Transfer UNE</th>
          </tr>
        </thead>
        <tbody>
          {transfers.map((t: FsaTransferDetail) => (
            <tr key={t.transfer_id} className="border-b border-border/20 hover:bg-muted/40" style={{ height: 32 }}>
              <td className="px-3 py-0 text-xs text-foreground">{formatDateTimeShort(t.sent_at)}</td>
              <td className="px-2 py-0 text-xs text-muted-foreground font-mono">{(t.bank_reference ?? '').slice(0, 14) || '—'}</td>
              <td className="px-2 py-0 text-center text-xs tabular-nums">{t.credits_count}</td>
              <td className="px-2 py-0 text-right tabular-nums text-xs font-semibold">{money(t.total_collected_from_client_mxn)}</td>
              <td className="px-2 py-0 text-right tabular-nums text-xs">{money(t.principal_mxn)}</td>
              <td className="px-2 py-0 text-right tabular-nums text-xs text-emerald-700 dark:text-emerald-400">{money(t.fsa_interest_mxn)}</td>
              <td className="px-2 py-0 text-right tabular-nums text-xs text-blue-700 dark:text-blue-400">{money(t.une_interest_mxn)}</td>
              <td className="px-2 py-0 text-right tabular-nums text-xs text-amber-700 dark:text-amber-400">{money(t.iva_mxn)}</td>
              <td className="px-2 py-0 text-right tabular-nums text-xs font-semibold text-blue-700 dark:text-blue-400">{money(t.transferred_to_une_mxn)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-border">
            <td className="px-3 py-1.5 text-xs font-semibold" colSpan={3}>Total {transfers.length} transferencias</td>
            <td className="px-2 py-1.5 text-right tabular-nums text-xs font-bold">{money(totals.collected)}</td>
            <td className="px-2 py-1.5 text-right tabular-nums text-xs font-semibold">{money(totals.principal)}</td>
            <td className="px-2 py-1.5 text-right tabular-nums text-xs font-semibold text-emerald-700 dark:text-emerald-400">{money(totals.fsa)}</td>
            <td className="px-2 py-1.5 text-right tabular-nums text-xs font-semibold text-blue-700 dark:text-blue-400">{money(totals.une)}</td>
            <td className="px-2 py-1.5 text-right tabular-nums text-xs font-semibold text-amber-700 dark:text-amber-400">{money(totals.iva)}</td>
            <td className="px-2 py-1.5 text-right tabular-nums text-xs font-bold text-blue-700 dark:text-blue-400">{money(totals.transferred)}</td>
          </tr>
        </tfoot>
      </table>
        </div>
      )}

      {/* Sugerencia de asiento contable */}
      <div className="border-t border-border bg-muted/20 px-3 py-3">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Sugerencia de asientos del mes</p>
        <table className="text-xs font-mono">
          <tbody>
            {fundings.length > 0 && (
              <>
                <tr><td className="pr-4 py-0.5">Débito Bancos (SPEI recibidos de UNE)</td><td className="text-right tabular-nums font-semibold">{money(totalFundingIn)}</td></tr>
                <tr><td className="pr-4 py-0.5 pl-4">Crédito Ctas x pagar UNE (fondeos por desembolsar)</td><td className="text-right tabular-nums">{money(totalFundingIn)}</td></tr>
                <tr><td colSpan={2}>&nbsp;</td></tr>
              </>
            )}
            {transfers.length > 0 && (
              <>
                <tr><td className="pr-4 py-0.5">Débito Bancos (cobros clientes)</td><td className="text-right tabular-nums font-semibold">{money(totals.collected)}</td></tr>
                <tr><td className="pr-4 py-0.5 pl-4">Crédito Ctas x pagar UNE (principal + Int UNE)</td><td className="text-right tabular-nums">{money(totals.transferred)}</td></tr>
                <tr><td className="pr-4 py-0.5 pl-4">Crédito Ingreso por intereses (Int FSA)</td><td className="text-right tabular-nums">{money(totals.fsa)}</td></tr>
                <tr><td className="pr-4 py-0.5 pl-4">Crédito IVA por pagar</td><td className="text-right tabular-nums">{money(totals.iva)}</td></tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── UNE Views ───────────────────────────────────────────────────────────

function UneMonthlyTable({ months, selectedYm, onSelect }: {
  months: UneMonthlySummary[]; selectedYm: string | null; onSelect: (ym: string) => void;
}) {
  if (months.length === 0) return <p className="py-8 text-center text-sm text-muted-foreground">Sin datos para el año seleccionado.</p>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-muted-foreground">
          <th className="px-3 py-1.5 font-medium">Mes</th>
          <th className="px-2 py-1.5 text-right font-medium">Fondeado (salidas)</th>
          <th className="px-2 py-1.5 text-right font-medium">Recibido (entradas)</th>
          <th className="px-2 py-1.5 text-right font-medium">Neto</th>
          <th className="px-2 py-1.5 text-right font-medium">Principal</th>
          <th className="px-2 py-1.5 text-right font-medium text-blue-700 dark:text-blue-400">Int UNE</th>
        </tr>
      </thead>
      <tbody>
        {months.map(m => (
          <tr key={m.yyyy_mm} onClick={() => onSelect(m.yyyy_mm)}
            className={`cursor-pointer border-b border-border/20 transition-colors ${m.yyyy_mm === selectedYm ? 'bg-primary/5' : 'hover:bg-muted/40'}`}
            style={{ height: 34 }}>
            <td className="whitespace-nowrap px-3 py-0 text-xs font-medium text-foreground">{formatYyyyMm(m.yyyy_mm)}</td>
            <td className="px-2 py-0 text-right tabular-nums text-xs text-rose-700 dark:text-rose-400">{money(m.funding_out_mxn)}</td>
            <td className="px-2 py-0 text-right tabular-nums text-xs text-emerald-700 dark:text-emerald-400">{money(m.cobros_in_mxn)}</td>
            <td className={`px-2 py-0 text-right tabular-nums text-xs font-semibold ${m.net_flow_mxn < 0 ? 'text-rose-700 dark:text-rose-400' : 'text-emerald-700 dark:text-emerald-400'}`}>{money(m.net_flow_mxn)}</td>
            <td className="px-2 py-0 text-right tabular-nums text-xs">{money(m.principal_received_mxn)}</td>
            <td className="px-2 py-0 text-right tabular-nums text-xs text-blue-700 dark:text-blue-400">{money(m.une_interest_received_mxn)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function UneMonthDetailPanel({ yyyyMm, data }: { yyyyMm: string; data: AccountingMonthDetailUneResponse | undefined }) {
  const fundings = data?.funding_out ?? [];
  const cobros = data?.cobros_in ?? [];

  const totalFunding = fundings.reduce((s, f) => s + f.total_amount_mxn, 0);
  const totalCobros = cobros.reduce((s, c) => s + c.total_received_mxn, 0);
  const totalPrincipal = cobros.reduce((s, c) => s + c.principal_mxn, 0);
  const totalUneInt = cobros.reduce((s, c) => s + c.une_interest_mxn, 0);

  const exportCsv = () => {
    const rows: (string | number)[][] = [
      ['Tipo', 'Fecha SPEI', 'ID', 'Ventana/Ref', 'Créditos/Ref', 'Monto', 'Principal', 'Int UNE'],
      ...fundings.map(f => ['FONDEO_SALIDA', f.sent_at, f.funding_id, f.window ?? '', f.credits_count, -f.total_amount_mxn, 0, 0]),
      ...cobros.map(c => ['COBRO_ENTRADA', c.sent_at, c.transfer_id, c.bank_reference ?? '', '', c.total_received_mxn, c.principal_mxn, c.une_interest_mxn]),
    ];
    downloadCsv(`libro-une-${yyyyMm}.csv`, rows);
  };

  const hasData = fundings.length > 0 || cobros.length > 0;
  if (!hasData) return <p className="py-12 text-center text-sm text-muted-foreground">Sin movimientos en {formatYyyyMm(yyyyMm)}.</p>;

  return (
    <div>
      <div className="flex items-center justify-between border-b border-border bg-muted/30 px-3 py-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Movimientos UNE de {formatYyyyMm(yyyyMm)}</h3>
        <Button variant="outline" onClick={exportCsv}><Download size={14} /> CSV</Button>
      </div>

      {/* Fondeos (salidas) */}
      {fundings.length > 0 && (
        <div>
          <div className="border-b border-border/60 bg-rose-50/40 dark:bg-rose-950/20 px-3 py-1.5">
            <span className="text-[10px] uppercase tracking-wider text-rose-700 dark:text-rose-400 font-medium">Fondeos SPEI UNE→FSA (salidas)</span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-1.5 font-medium">Fecha SPEI</th>
                <th className="px-2 py-1.5 font-medium">Funding ID</th>
                <th className="px-2 py-1.5 font-medium">Ventana</th>
                <th className="px-2 py-1.5 text-center font-medium">Créd</th>
                <th className="px-2 py-1.5 font-medium">Ref bancaria</th>
                <th className="px-2 py-1.5 text-right font-medium text-rose-700 dark:text-rose-400">Monto</th>
              </tr>
            </thead>
            <tbody>
              {fundings.map((f: UneFundingOutDetail) => (
                <tr key={f.funding_id} className="border-b border-border/20" style={{ height: 32 }}>
                  <td className="px-3 py-0 text-xs">{formatDateTimeShort(f.sent_at)}</td>
                  <td className="px-2 py-0 text-xs font-mono text-muted-foreground">{f.funding_id.slice(0, 8)}...</td>
                  <td className="px-2 py-0 text-xs">{f.window ?? '—'}</td>
                  <td className="px-2 py-0 text-center text-xs tabular-nums">{f.credits_count}</td>
                  <td className="px-2 py-0 text-xs font-mono text-muted-foreground">{(f.bank_reference ?? '').slice(0, 14) || '—'}</td>
                  <td className="px-2 py-0 text-right tabular-nums text-xs font-semibold text-rose-700 dark:text-rose-400">{money(f.total_amount_mxn)}</td>
                </tr>
              ))}
              <tr className="border-t border-border/40 bg-rose-50/20 dark:bg-rose-950/10">
                <td className="px-3 py-1.5 text-xs font-semibold" colSpan={5}>Total fondeos ({fundings.length})</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-xs font-bold text-rose-700 dark:text-rose-400">{money(totalFunding)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Cobros (entradas) */}
      {cobros.length > 0 && (
        <div>
          <div className="border-t border-b border-border/60 bg-emerald-50/40 dark:bg-emerald-950/20 px-3 py-1.5">
            <span className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400 font-medium">Cobros SPEI FSA→UNE (entradas)</span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-1.5 font-medium">Fecha SPEI</th>
                <th className="px-2 py-1.5 font-medium">Transfer ID</th>
                <th className="px-2 py-1.5 font-medium">Ref bancaria</th>
                <th className="px-2 py-1.5 text-right font-medium">Principal</th>
                <th className="px-2 py-1.5 text-right font-medium text-blue-700 dark:text-blue-400">Int UNE</th>
                <th className="px-2 py-1.5 text-right font-medium text-emerald-700 dark:text-emerald-400">Total recibido</th>
              </tr>
            </thead>
            <tbody>
              {cobros.map((c: UneCobroInDetail) => (
                <tr key={c.transfer_id} className="border-b border-border/20" style={{ height: 32 }}>
                  <td className="px-3 py-0 text-xs">{formatDateTimeShort(c.sent_at)}</td>
                  <td className="px-2 py-0 text-xs font-mono text-muted-foreground">{c.transfer_id.slice(0, 8)}...</td>
                  <td className="px-2 py-0 text-xs font-mono text-muted-foreground">{(c.bank_reference ?? '').slice(0, 14) || '—'}</td>
                  <td className="px-2 py-0 text-right tabular-nums text-xs">{money(c.principal_mxn)}</td>
                  <td className="px-2 py-0 text-right tabular-nums text-xs text-blue-700 dark:text-blue-400">{money(c.une_interest_mxn)}</td>
                  <td className="px-2 py-0 text-right tabular-nums text-xs font-semibold text-emerald-700 dark:text-emerald-400">{money(c.total_received_mxn)}</td>
                </tr>
              ))}
              <tr className="border-t border-border/40 bg-emerald-50/20 dark:bg-emerald-950/10">
                <td className="px-3 py-1.5 text-xs font-semibold" colSpan={3}>Total cobros ({cobros.length})</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-xs font-semibold">{money(totalPrincipal)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-xs font-semibold text-blue-700 dark:text-blue-400">{money(totalUneInt)}</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-xs font-bold text-emerald-700 dark:text-emerald-400">{money(totalCobros)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Sugerencia de asiento contable */}
      <div className="border-t border-border bg-muted/20 px-3 py-3">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Sugerencia de asientos del mes</p>
        <table className="text-xs font-mono">
          <tbody>
            {fundings.length > 0 && (
              <>
                <tr><td className="pr-4 py-0.5">Débito Cta x cobrar FSA (fondeos otorgados)</td><td className="text-right tabular-nums font-semibold">{money(totalFunding)}</td></tr>
                <tr><td className="pr-4 py-0.5 pl-4">Crédito Bancos (SPEI enviados)</td><td className="text-right tabular-nums">{money(totalFunding)}</td></tr>
                <tr><td colSpan={2}>&nbsp;</td></tr>
              </>
            )}
            {cobros.length > 0 && (
              <>
                <tr><td className="pr-4 py-0.5">Débito Bancos (SPEI recibidos)</td><td className="text-right tabular-nums font-semibold">{money(totalCobros)}</td></tr>
                <tr><td className="pr-4 py-0.5 pl-4">Crédito Cta x cobrar FSA (principal amortizado)</td><td className="text-right tabular-nums">{money(totalPrincipal)}</td></tr>
                <tr><td className="pr-4 py-0.5 pl-4">Crédito Ingreso por intereses (exento LIVA Art. 15-X)</td><td className="text-right tabular-nums">{money(totalUneInt)}</td></tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────

export function CierreMensualPage() {
  const { resolvedRole } = useAudience();
  const scope: 'FSA' | 'UNE' = resolvedRole === 'BACKOFFICE' ? 'FSA' : 'UNE';
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYear);
  const [selectedYm, setSelectedYm] = useState<string | null>(null);

  const monthlyQuery = useQuery<any>({
    queryKey: ['accounting', 'monthly', scope, year],
    queryFn: () => scope === 'FSA'
      ? getAccountingMonthly('FSA', PARTNER_ID, year)
      : getAccountingMonthly('UNE', PARTNER_ID, year),
  });

  const detailQuery = useQuery<any>({
    queryKey: ['accounting', 'detail', scope, selectedYm ?? 'none'],
    queryFn: () => selectedYm
      ? (scope === 'FSA'
        ? getAccountingMonthDetail('FSA', PARTNER_ID, selectedYm)
        : getAccountingMonthDetail('UNE', PARTNER_ID, selectedYm))
      : Promise.resolve(null),
    enabled: !!selectedYm,
  });

  const monthly = monthlyQuery.data?.data as AccountingMonthlyFsaResponse | AccountingMonthlyUneResponse | undefined;
  const detail = detailQuery.data?.data as AccountingMonthDetailFsaResponse | AccountingMonthDetailUneResponse | undefined;

  const years = useMemo(() => {
    const arr: number[] = [];
    for (let y = currentYear; y >= currentYear - 3; y--) arr.push(y);
    return arr;
  }, [currentYear]);

  const scopeLabel = scope === 'FSA' ? 'Libro Fiado SA' : 'Libro UNE-Credito';

  return (
    <div>
      <PageHeader
        title="Cierre Mensual"
        helpKey={scope === 'FSA' ? 'cierre-mensual-fsa' : 'cierre-mensual-une'}
        subtitle={`${scopeLabel} · Base flujo (por fecha SPEI)`}
        breadcrumbs={BREADCRUMBS}
        actions={
          <div className="flex gap-2 items-center">
            <select
              value={year}
              onChange={e => { setYear(parseInt(e.target.value, 10)); setSelectedYm(null); }}
              className="rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground"
            >
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <Button onClick={() => { void monthlyQuery.refetch(); void detailQuery.refetch(); }}>
              <RefreshCw size={16} /> Actualizar
            </Button>
          </div>
        }
      />

      <div className={`grid grid-cols-1 gap-4 ${scope === 'FSA' ? 'lg:grid-cols-[minmax(520px,680px)_1fr]' : 'lg:grid-cols-[520px_1fr]'}`}>
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          <div className="border-b border-border bg-muted/30 px-3 py-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {scopeLabel} — {year}
            </h3>
          </div>
          {monthlyQuery.isLoading && <p className="py-8 text-center text-sm text-muted-foreground">Cargando...</p>}
          {monthlyQuery.isError && <p className="py-8 text-center text-sm text-rose-600">Error al cargar el reporte mensual.</p>}
          {!monthlyQuery.isLoading && monthly && scope === 'FSA' && (
            <FsaMonthlyTable months={(monthly as AccountingMonthlyFsaResponse).months} selectedYm={selectedYm} onSelect={setSelectedYm} />
          )}
          {!monthlyQuery.isLoading && monthly && scope === 'UNE' && (
            <UneMonthlyTable months={(monthly as AccountingMonthlyUneResponse).months} selectedYm={selectedYm} onSelect={setSelectedYm} />
          )}
        </div>

        <div className="rounded-lg border border-border bg-card overflow-hidden">
          {!selectedYm && <p className="py-12 text-center text-sm text-muted-foreground">Selecciona un mes para ver el detalle.</p>}
          {selectedYm && detailQuery.isLoading && <p className="py-8 text-center text-sm text-muted-foreground">Cargando detalle...</p>}
          {selectedYm && detail && scope === 'FSA' && (
            <FsaMonthDetailPanel yyyyMm={selectedYm} data={detail as AccountingMonthDetailFsaResponse} />
          )}
          {selectedYm && detail && scope === 'UNE' && (
            <UneMonthDetailPanel yyyyMm={selectedYm} data={detail as AccountingMonthDetailUneResponse} />
          )}
        </div>
      </div>
    </div>
  );
}
